<script setup lang="ts">
import type { CmsElementBuyBox } from "@shopware/composables";
import { getTranslatedProperty } from "@shopware/helpers";
import { useCmsTranslations } from "@shopware/composables";
import { getProductName } from "~/components/utilities";
import { defu } from "defu";
import { computed } from "vue";
import {
  useCmsElementConfig,
  usePrice,
  useProduct,
  useProductPrice,
  useSessionContext,
} from "#imports";
import type { Schemas } from "#shopware";
import { productDetailTracking } from "~/components/utilities/GtmTrackings";

const props = defineProps<{
  content: CmsElementBuyBox;
}>();

type Translations = {
  product: {
    previously: string;
    amount: string;
    price: {
      [key: string]: string;
    };
    to: string;
    from: string;
    content: string;
    pricesIncl: string;
    pricesExcl: string;
    deliveryTime: string;
    days: string;
    noAvailable: string;
    productNumber: string;
  };
};

let translations: Translations = {
  product: {
    previously: "Previously",
    amount: "Amount",
    price: {
      label: "Price",
      to: "To",
      from: "From",
    },
    to: "To",
    from: "From",
    content: "Content",
    pricesIncl: "Preise inkl. MwSt.",
    pricesExcl: "Preise exkl. MwSt.",
    deliveryTime: "Verfügbar, Lieferzeit",
    days: "days",
    noAvailable: "No longer available",
    productNumber: "Product number",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const { getConfigValue } = useCmsElementConfig(props.content);
const alignment = computed(() => getConfigValue("alignment"));

const { taxState, currency } = useSessionContext();

const { product, changeVariant } = useProduct(
  props.content.data.product,
  props.content.data.configuratorSettings || []
);

const { unitPrice, price, tierPrices, isListPrice } = useProductPrice(product);
const regulationPrice = computed(() => price.value?.regulationPrice?.price);
const { getFormattedPrice } = usePrice();

const referencePrice = computed(
  () => product.value?.calculatedPrice?.referencePrice
);
const productNumber = computed(() => product.value?.productNumber);
const purchaseUnit = computed(() => product.value?.purchaseUnit);
const unitName = computed(() => product.value?.unit?.name);
const availableStock = computed(() => product.value?.availableStock ?? 0);
const minPurchase = computed(() => product.value?.minPurchase ?? 0);
const deliveryTime = computed(() => product.value?.deliveryTime);
const restockTime = computed(() => product.value?.restockTime);

const productSubTitle = computed(
  () => product.value?.translated?.customFields?.customfields_product_subtitle
);

const productFeatures = computed(() =>
  (product.value?.translated?.customFields?.zenit_stratus_features || "")
    .split(";")
    .map((feature: string) => feature.trim())
);

const priceComparison = computed(
  () => product.value?.translated?.customFields?.customfields_price_comparison
);

const { reviews } = useProductReviews();
const paymentMethods =
  inject<ComputedRef<Schemas["PaymentMethod"][]>>("paymentMethods");

if (import.meta.client) {
  productDetailTracking(product.value);
}
</script>
<template>
  <div
    v-if="product"
    :class="{
      'h-full flex flex-col': true,
      'justify-start': alignment === 'flex-start',
      'justify-end': alignment === 'flex-end',
      'justify-center': alignment === 'center',
    }"
  >
    <div>
      <h6 class="mb-4">
        {{ getTranslatedProperty(product.manufacturer, "name") }}
      </h6>
      <h1 class="inner-h1 mb-2">
        {{ getProductName(product) }}
      </h1>
      <div v-if="productSubTitle" class="mb-10">
        <div v-html="productSubTitle"></div>
      </div>

      <div
        v-if="reviews?.length > 0"
        class="mb-4 flex items-center w-full gap-4"
      >
        <ClientOnly>
          <div class="rating">
            <span class="stars flex w-full h-auto">
              <i
                v-for="i in 5"
                class="fa-star text-cust_green"
                :class="{
                  'fa-regular': i > (product?.ratingAverage ?? 0),
                  fa: i <= (product?.ratingAverage ?? 0),
                }"
              ></i>
            </span>
          </div>
          <p class="m-0">
            {{ reviews?.length }}
            {{ $t("product.reviews.label", reviews?.length) }}
          </p>
        </ClientOnly>
      </div>
      <ClientOnly>
        <div
          v-if="reviews?.length > 0"
          class="flex items-center mt-4 mb-8 xl:mb-10 gap-4"
        >
          <div class="w-[60px] md:w-auto">
            <img src="assets/images/rev-icon.png" alt="" />
          </div>
          <div
            class="w-[calc(100%-60px)] md:w-auto rounded-xl md:rounded-3xl bg-cust_cream px-2 md:px-4 py-[6px] font-redit_sans_con text-sm md:text-[15px] font-light"
          >
            <strong class="font-semibold">Renzo Blumenthal</strong>
            <template v-if="reviews?.length > 1">
              {{ $t("product.reviews.reviewCountOthers", reviews?.length) }}
            </template>
            {{
              $t("plubel.product.reviews.reviewCountLoves", {
                productName: getTranslatedProperty(product, "name"),
              })
            }}
          </div>
        </div>
      </ClientOnly>
      <ul v-if="productFeatures.length > 0" class="listing">
        <template v-for="feature in productFeatures">
          <li>{{ feature }}</li>
        </template>
      </ul>
      <ProductDetailSetConfiguration />
      <div
        v-if="tierPrices.length <= 1"
        class="flex flex-wrap items-start gap-x-6 mb-8 xl:mb-12 mt-8 xl:mt-10"
      >
        <h3 v-if="isListPrice" class="mb-0">
          <SharedPrice
            class="line-through font-normal"
            :value="price?.listPrice?.price"
          />
        </h3>
        <h3 v-if="unitPrice" class="mb-0">
          <SharedPrice
            class="justify-start"
            :class="{
              'text-red': isListPrice,
            }"
            :value="unitPrice"
          />
        </h3>
        <h3 v-if="regulationPrice" class="mb-0">
          <div
            class="font-normal text-base tracking-wide flex text-secondary-500"
          >
            {{ $t("product.previously") }}
            <SharedPrice class="ml-1" :value="regulationPrice" />
          </div>
        </h3>
        <span
          v-if="priceComparison"
          class="block rounded-3xl bg-cust_cream px-4 py-[6px] font-redit_sans text-[15px] font-light"
        >
          {{ priceComparison }}
        </span>
        <p class="w-full mt-2 md:mt-4 text-15px">
          <template v-if="taxState === 'gross'">
            {{ $t("product.pricesIncl") }}
          </template>
          <template v-else> {{ $t("product.pricesExcl") }} </template>
        </p>
      </div>
      <div v-else>
        <table class="border-collapse table-auto w-full text-sm mb-8">
          <thead>
            <tr>
              <th
                class="border-b dark:border-secondary-600 font-medium p-4 pl-8 pt-0 pb-3 text-secondary-600 dark:text-secondary-200 text-left"
              >
                {{ $t("product.amount") }}
              </th>

              <th
                class="border-b dark:border-secondary-600 font-medium p-4 pr-8 pt-0 pb-3 text-secondary-600 dark:text-secondary-200 text-left"
              >
                {{ $t("product.price.label") }}
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-secondary-800">
            <tr v-for="(tierPrice, index) in tierPrices" :key="tierPrice.label">
              <td
                class="border-b border-secondary-100 dark:border-secondary-700 p-4 pl-8 font-medium text-secondary-500 dark:text-secondary-400"
              >
                <span v-if="index < tierPrices.length - 1">
                  {{ $t("product.to") }}
                </span>
                <span v-else>{{ $t("product.from") }}</span>
                {{ tierPrice.quantity }}
              </td>
              <td
                class="border-b border-secondary-100 dark:border-secondary-700 p-4 pr-8 font-medium text-current-500 dark:text-secondary-400"
              >
                {{ getFormattedPrice(tierPrice.unitPrice) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <ProductDetailVariantConfigurator @change="changeVariant" />
    <div class="mt-4">
      <span
        v-if="availableStock >= minPurchase && deliveryTime"
        class="ps-10 py-[2px] bg-[url('assets/images/lieferbar.svg')] bg-left bg-no-repeat text-base md:text-lg"
      >
        {{ $t("product.deliveryTime") }} {{ deliveryTime?.name }}
      </span>
      <span
        v-else-if="availableStock < minPurchase && deliveryTime && restockTime"
        class="ps-10 py-[2px] bg-[url('assets/images/lieferbar.svg')] bg-left bg-no-repeat text-base md:text-lg"
      >
        {{ $t("product.deliveryTime") }} {{ restockTime }}
        {{ $t("product.days") }} {{ deliveryTime?.name }}</span
      >
      <span
        v-else
        class="flex items-center py-[2px] bg-no-repeat text-base md:text-lg"
      >
        <i class="fa fa-circle-xmark text-2xl text-red-600 align-middle"></i>
        <span class="ml-2">{{ $t("product.noAvailable") }}</span>
      </span>
    </div>
    <ProductDetailAddToCart :product="product" />
    <ul
      class="flex flex-col items-center sm:flex-row flex-wrap gap-y-1 gap-x-4 md:gap-3 lg:gap-6 justify-center md:justify-start prodList"
    >
      <li
        class="bg-[url(assets/images/umweltfreundlich.svg)] bg-no-repeat bg-left ps-7 md:ps-10 text-lg font-redit_sans_con"
      >
        {{ $t("plubel.product.features.ecoFriendlyColors") }}
      </li>
      <li
        class="bg-[url(assets/images/lieferung.svg)] bg-no-repeat bg-left ps-7 md:ps-10 text-lg font-redit_sans_con"
      >
        {{ $t("plubel.product.features.fastShipping") }}
      </li>
      <li
        class="bg-[url(assets/images/heart.svg)] bg-no-repeat bg-left ps-7 md:ps-10 text-lg font-redit_sans_con"
      >
        {{ $t("plubel.product.features.swissOnlineShop") }}
      </li>
    </ul>

    <ProductDetailDescriptionReviews />

    <div class="mt-10 md:mt-12">
      <ul class="flex gap-4 mb-6">
        <template v-for="paymentMethod in paymentMethods">
          <li>
            <NuxtImg
              format="webp"
              loading="lazy"
              class="h-8 w-auto object-contain"
              :src="paymentMethod?.media?.url"
              :alt="paymentMethod?.media?.alt || paymentMethod?.name"
              :width="paymentMethod?.media?.metaData?.width"
              :height="paymentMethod?.media?.metaData?.height"
            />
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>
